import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { formatPnl, pnlColor } from '@/lib/format';
import type { BacktestTradeRecord } from '@/types/api';

const PAGE_SIZE = 50;

export function formatDuration(ms: number): string {
  if (ms < 60000) return `${(ms / 1000).toFixed(0)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(0)}m`;
  if (ms < 86400000) return `${(ms / 3600000).toFixed(1)}h`;
  return `${(ms / 86400000).toFixed(1)}d`;
}

type TradeFilter = 'all' | 'buy' | 'sell' | 'win' | 'loss';

interface TradeHistoryModalProps {
  open: boolean;
  onClose: () => void;
  trades: BacktestTradeRecord[];
  totalTrades: number;
  netProfit: number;
}

export function TradeHistoryModal({ open, onClose, trades, totalTrades, netProfit }: TradeHistoryModalProps) {
  const [tradePage, setTradePage] = useState(0);
  const [tradeFilter, setTradeFilter] = useState<TradeFilter>('all');

  if (!open) return null;

  const filteredTrades = trades.filter(t => {
    if (tradeFilter === 'buy') return t.type === 'BUY';
    if (tradeFilter === 'sell') return t.type === 'SELL';
    if (tradeFilter === 'win') return t.netPnl > 0;
    if (tradeFilter === 'loss') return t.netPnl <= 0;
    return true;
  });
  const totalPages = Math.ceil(filteredTrades.length / PAGE_SIZE);
  const pageTrades = filteredTrades.slice(tradePage * PAGE_SIZE, (tradePage + 1) * PAGE_SIZE);
  const globalOffset = tradeFilter === 'all' && totalTrades > trades.length
    ? totalTrades - trades.length
    : 0;
  const wins = trades.filter(t => t.netPnl > 0).length;
  const losses = trades.length - wins;

  return (
    <Modal
      open
      onClose={() => { setTradeFilter('all'); setTradePage(0); onClose(); }}
      title={`Trade History (${tradeFilter === 'all' ? totalTrades : `${filteredTrades.length} of ${totalTrades}`})`}
      width="max-w-2xl"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-3">
          <span className="text-[10px] text-ink-3">
            <span className="font-semibold text-green">{wins}W</span>
            {' / '}
            <span className="font-semibold text-red">{losses}L</span>
          </span>
          <span className="text-[10px] text-ink-4">|</span>
          <span className={`text-[10px] font-semibold font-mono ${pnlColor(netProfit)}`}>
            Net: {formatPnl(netProfit)}
          </span>
        </div>
        <div className="flex gap-1">
          {(['all', 'buy', 'sell', 'win', 'loss'] as const).map(f => (
            <button
              key={f}
              type="button"
              onClick={() => { setTradeFilter(f); setTradePage(0); }}
              className={`text-[9px] font-bold px-2 py-0.5 rounded-md transition-all cursor-pointer capitalize ${
                tradeFilter === f
                  ? 'bg-orange-bg text-orange border border-orange/20'
                  : 'bg-surface text-ink-4 border border-glass-border hover:text-ink-3'
              }`}
            >
              {f === 'all' ? `All (${trades.length})` : f === 'buy' ? `Buy (${trades.filter(t => t.type === 'BUY').length})` : f === 'sell' ? `Sell (${trades.filter(t => t.type === 'SELL').length})` : f === 'win' ? `Win (${wins})` : `Loss (${losses})`}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="text-ink-4 uppercase tracking-wider border-b border-border">
              <th className="text-left py-1.5 px-1 font-semibold">#</th>
              <th className="text-left py-1.5 px-1 font-semibold">Type</th>
              <th className="text-left py-1.5 px-1 font-semibold">Open</th>
              <th className="text-left py-1.5 px-1 font-semibold">Close</th>
              <th className="text-right py-1.5 px-1 font-semibold">Vol</th>
              <th className="text-right py-1.5 px-1 font-semibold">Gross</th>
              <th className="text-right py-1.5 px-1 font-semibold">Net P&L</th>
              <th className="text-right py-1.5 px-1 font-semibold">Duration</th>
            </tr>
          </thead>
          <tbody>
            {pageTrades.map((t, i) => {
              const rowNum = globalOffset + tradePage * PAGE_SIZE + i + 1;
              return (
                <tr key={i} className="border-b border-border/50 hover:bg-surface/30">
                  <td className="py-1.5 px-1 font-mono text-ink-4">{rowNum}</td>
                  <td className="py-1.5 px-1">
                    <Badge color={t.type === 'BUY' ? 'green' : 'red'} className="text-[8px]">
                      {t.type}
                    </Badge>
                  </td>
                  <td className="py-1.5 px-1 font-mono text-ink-3">{t.openPrice.toFixed(2)}</td>
                  <td className="py-1.5 px-1 font-mono text-ink-3">{t.closePrice.toFixed(2)}</td>
                  <td className="py-1.5 px-1 font-mono text-ink-4 text-right">{t.volume}</td>
                  <td className={`py-1.5 px-1 font-mono text-right ${pnlColor(t.grossPnl)}`}>
                    {formatPnl(t.grossPnl)}
                  </td>
                  <td className={`py-1.5 px-1 font-mono font-bold text-right ${pnlColor(t.netPnl)}`}>
                    {formatPnl(t.netPnl)}
                  </td>
                  <td className="py-1.5 px-1 font-mono text-ink-4 text-right">
                    {t.closeTime > 0 && t.openTime > 0 ? formatDuration(t.closeTime - t.openTime) : '\u2014'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
          <button
            onClick={() => setTradePage(p => Math.max(0, p - 1))}
            disabled={tradePage === 0}
            className="flex items-center gap-1 text-[10px] font-semibold text-ink-3 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            <ChevronLeft size={12} />
            Prev
          </button>
          <span className="text-[10px] text-ink-4">
            Page {tradePage + 1} of {totalPages}
          </span>
          <button
            onClick={() => setTradePage(p => Math.min(totalPages - 1, p + 1))}
            disabled={tradePage >= totalPages - 1}
            className="flex items-center gap-1 text-[10px] font-semibold text-ink-3 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            Next
            <ChevronRight size={12} />
          </button>
        </div>
      )}
    </Modal>
  );
}
